import type { assetAvailabilityReducer } from "./features/assetAvailabilitySlice/assetAvailabilitySlice";
import type { assetAvailabilityApi } from "./features/assetAvailabilitySlice/services/assetAvailabilityApi";
import type { assetSimulatorsApi } from "./features/assetSimulatorsSlice/services/assetSimulatorsApi";
import type { availabilityPlanningReducer } from "./features/availabilityPlanningSlice/availabilityPlanningSlice";
import type { availabilityExchangeApi } from "./features/availabilityPlanningSlice/services/availabilityExchangeApi";
import type { availabilityPlanningApi } from "./features/availabilityPlanningSlice/services/availabilityPlanningApi";
import type { availabilityPlanningEntityApi } from "./features/availabilityPlanningSlice/services/availabilityPlanningEntityApi";
import type { bmAutomationApi } from "./features/bmAutomation/bmAutomationApi";
import type { bmAutomationReducer } from "./features/bmAutomation/bmAutomationSlice";
import type { bmAutomationWsApi } from "./features/bmAutomation/bmAutomationWsApi";
import type { bmEntityExchangeApi } from "./features/bmAutomation/bmEntityExchangeApi";
import type { bmParameterAutomationApi } from "./features/bmAutomation/bmParameterAutomationApi";
import type { bodExchangeApi } from "./features/bmAutomation/bodExchangeApi";
import type { bmInputApi } from "./features/bmInputSlice/bmInputApi";
import type { BmInstructionsRootStateType } from "./features/bmInstructionsSlice";
import type { bmInstructionExchangeApi } from "./features/bmInstructionsSlice/services/bmInstructionExchangeApi";
import type { DataExporterRootStateType } from "./features/dataExporterSlice";
import type { esoSimulatorApi } from "./features/esoSimulatorSlice/services/esoSimulatorApi";
import type { faultExchangeApi } from "./features/faultExchangeSlice/services/faultExchangeApi";
import type { pagerDutySimulatorApi } from "./features/pagerDutySimulatorSlice/services/pagerDutySimulatorApi";
import type { PortfolioExplorerRootStateType } from "./features/portfolioExplorerSlice";
import type { ScheduleExchangeRootStateType } from "./features/powerRootSlice";
import type { blockInputApi } from "./features/powerRootSlice/services/blockInputApi";
import type { createBlockScheduleApi } from "./features/powerRootSlice/services/createBlockScheduleApi";
import type { createPowerScheduleApi } from "./features/powerRootSlice/services/createScheduleApi";
import type { schedulePowerApi } from "./features/powerRootSlice/services/powerApi";
import type { ScheduleEnergyRootStateType } from "./features/scheduleEnergyRootSlice";
import type { energyExchangeApi } from "./features/scheduleEnergyRootSlice/services/energyExchangeApi";
import type { energyScheduleExchangeApi } from "./features/scheduleEnergyRootSlice/services/energyScheduleExchangeApi";
import type { entityExchangeApi } from "./features/scheduleEnergyRootSlice/services/entityExchangeApi";
import type { profileCreatorApi } from "./features/scheduleEnergyRootSlice/services/profileCreatorApi";
import type { SchedulingHubRootStateType } from "./features/schedulingHubSlice";
import type { TradeBlotterRootStateType } from "./features/tradeBlotterSlice";
import type { combinedReporterApi } from "./features/tradeBlotterSlice/services/combinedReporterApi";
import type { summaryReporterApi } from "./features/tradeBlotterSlice/services/summaryReporterApi";
import type { TradeHubRootStateType } from "./features/tradeHubSlice";
import type { dayAheadConfigApi } from "./features/tradeHubSlice/services/dayAheadConfigApi";
import type { dayAheadWholesaleApi } from "./features/tradeHubSlice/services/dayAheadWholesaleApi";
import type { predictionApi } from "./features/tradingPredictions/api/predictionApi";

export const dynamicSlicesNamesKeys = {
  POWER: "POWER",
  PORTFOLIO_EXPLORER: "PORTFOLIO_EXPLORER",
  SCHEDULE_ENERGY: "SCHEDULE_ENERGY",
  BM_INSTRUCTIONS: "BM_INSTRUCTIONS",
  BM_AUTOMATION: "BM_AUTOMATION",
  TRADE_HUB: "TRADE_HUB",
  AVAILABILITY_PLANNING: "AVAILABILITY_PLANNING",
  TRADE_BLOTTER: "TRADE_BLOTTER",
  DATA_EXPORTER: "DATA_EXPORTER",
  SCHEDULING_HUB: "SCHEDULING_HUB",
  ASSET_AVAILABILITY: "ASSET_AVAILABILITY",
} as const;

// used to map the dynamic slices to the reducer path
export const dynamicSlicesNames = {
  [dynamicSlicesNamesKeys.POWER]: "power",
  [dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER]: "portfolioExplorer",
  [dynamicSlicesNamesKeys.SCHEDULE_ENERGY]: "scheduleEnergy",
  [dynamicSlicesNamesKeys.BM_INSTRUCTIONS]: "bmInstructions",
  [dynamicSlicesNamesKeys.BM_AUTOMATION]: "bmAutomation",
  [dynamicSlicesNamesKeys.TRADE_HUB]: "tradeHub",
  [dynamicSlicesNamesKeys.AVAILABILITY_PLANNING]: "availabilityPlanning",
  [dynamicSlicesNamesKeys.TRADE_BLOTTER]: "tradeBlotter",
  [dynamicSlicesNamesKeys.DATA_EXPORTER]: "dataExporter",
  [dynamicSlicesNamesKeys.SCHEDULING_HUB]: "schedulingHub",
  [dynamicSlicesNamesKeys.ASSET_AVAILABILITY]: "assetAvailability",
} as const;

export type DynamicSlicesNames = typeof dynamicSlicesNames;

type RtkQueryApis = {
  [schedulePowerApi.reducerPath]: ReturnType<typeof schedulePowerApi.reducer>;
  [createPowerScheduleApi.reducerPath]: ReturnType<
    typeof createPowerScheduleApi.reducer
  >;
  [createBlockScheduleApi.reducerPath]: ReturnType<
    typeof createBlockScheduleApi.reducer
  >;
  //TODO: Remove this once block input is removed
  [blockInputApi.reducerPath]: ReturnType<typeof blockInputApi.reducer>;
  [bmInstructionExchangeApi.reducerPath]: ReturnType<
    typeof bmInstructionExchangeApi.reducer
  >;
  [bmAutomationApi.reducerPath]: ReturnType<typeof bmAutomationApi.reducer>;
  [bmAutomationWsApi.reducerPath]: ReturnType<typeof bmAutomationWsApi.reducer>;
  [bodExchangeApi.reducerPath]: ReturnType<typeof bodExchangeApi.reducer>;
  [bmInputApi.reducerPath]: ReturnType<typeof bmInputApi.reducer>;
  [bmEntityExchangeApi.reducerPath]: ReturnType<
    typeof bmEntityExchangeApi.reducer
  >;
  [bmParameterAutomationApi.reducerPath]: ReturnType<
    typeof bmParameterAutomationApi.reducer
  >;
  [energyExchangeApi.reducerPath]: ReturnType<typeof energyExchangeApi.reducer>;
  [entityExchangeApi.reducerPath]: ReturnType<typeof entityExchangeApi.reducer>;
  [profileCreatorApi.reducerPath]: ReturnType<typeof profileCreatorApi.reducer>;
  [energyScheduleExchangeApi.reducerPath]: ReturnType<
    typeof energyScheduleExchangeApi.reducer
  >;
  [faultExchangeApi.reducerPath]: ReturnType<typeof faultExchangeApi.reducer>;
  [dayAheadConfigApi.reducerPath]: ReturnType<typeof dayAheadConfigApi.reducer>;
  [dayAheadWholesaleApi.reducerPath]: ReturnType<
    typeof dayAheadWholesaleApi.reducer
  >;
  [predictionApi.reducerPath]: ReturnType<typeof predictionApi.reducer>;
  [availabilityPlanningApi.reducerPath]: ReturnType<
    typeof availabilityPlanningApi.reducer
  >;
  [availabilityExchangeApi.reducerPath]: ReturnType<
    typeof availabilityExchangeApi.reducer
  >;
  [availabilityPlanningEntityApi.reducerPath]: ReturnType<
    typeof availabilityPlanningEntityApi.reducer
  >;
  [combinedReporterApi.reducerPath]: ReturnType<
    typeof combinedReporterApi.reducer
  >;
  [summaryReporterApi.reducerPath]: ReturnType<
    typeof summaryReporterApi.reducer
  >;
  [esoSimulatorApi.reducerPath]: ReturnType<typeof esoSimulatorApi.reducer>;
  [pagerDutySimulatorApi.reducerPath]: ReturnType<
    typeof pagerDutySimulatorApi.reducer
  >;
  [assetSimulatorsApi.reducerPath]: ReturnType<
    typeof assetSimulatorsApi.reducer
  >;
  [assetAvailabilityApi.reducerPath]: ReturnType<
    typeof assetAvailabilityApi.reducer
  >;
};

export type LazyLoadedSlices = {
  // Regular slices
  [dynamicSlicesNames.POWER]: ScheduleExchangeRootStateType;
  [dynamicSlicesNames.SCHEDULE_ENERGY]: ScheduleEnergyRootStateType;
  [dynamicSlicesNames.BM_INSTRUCTIONS]: BmInstructionsRootStateType;
  [dynamicSlicesNames.BM_AUTOMATION]: ReturnType<typeof bmAutomationReducer>;
  [dynamicSlicesNames.PORTFOLIO_EXPLORER]: PortfolioExplorerRootStateType;
  [dynamicSlicesNames.TRADE_HUB]: TradeHubRootStateType;
  [dynamicSlicesNames.AVAILABILITY_PLANNING]: ReturnType<
    typeof availabilityPlanningReducer
  >;
  [dynamicSlicesNames.TRADE_BLOTTER]: TradeBlotterRootStateType;
  [dynamicSlicesNames.DATA_EXPORTER]: DataExporterRootStateType;
  [dynamicSlicesNames.SCHEDULING_HUB]: SchedulingHubRootStateType;
  [dynamicSlicesNames.ASSET_AVAILABILITY]: ReturnType<
    typeof assetAvailabilityReducer
  >;
} & RtkQueryApis;
